import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  openReportEmail,
  buildReportClipboard,
  STORY_ISSUE_TYPES,
  LESSON_ISSUE_TYPES,
} from '../../services/reportService';
import AuthForm from '../auth/AuthForm';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 90,
  padding: 16,
};

const cardStyle = {
  background: 'var(--color-background-primary, var(--color-bg-card))',
  borderRadius: 'var(--border-radius-lg, 16px)',
  padding: '1.5rem',
  maxWidth: 440,
  width: 'calc(100% - 32px)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  maxHeight: '90dvh',
  overflowY: 'auto',
};

const titleStyle = {
  margin: '12px 0 8px',
  fontSize: '1.1rem',
  fontWeight: 800,
  color: 'var(--color-text)',
  letterSpacing: '-0.02em',
};

const bodyTextStyle = {
  margin: '0 0 16px',
  fontSize: 14,
  lineHeight: 1.55,
  color: 'var(--color-text-secondary, var(--color-text))',
};

const subtitleStyle = {
  margin: '0 0 14px',
  fontSize: 12,
  color: 'var(--color-text-secondary, #888)',
};

const cancelLinkStyle = {
  display: 'block',
  textAlign: 'center',
  background: 'none',
  border: 'none',
  fontSize: 13,
  color: 'var(--color-text-secondary, #888)',
  cursor: 'pointer',
  padding: '6px 0 0',
  fontFamily: 'inherit',
};

const actionsRowStyle = {
  display: 'flex',
  gap: 10,
  justifyContent: 'flex-end',
  marginTop: 16,
};

const secondaryBtnStyle = {
  padding: '10px 16px',
  borderRadius: 12,
  border: '1px solid var(--color-border)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const noteStyle = {
  marginTop: 8,
  fontSize: 11,
  color: 'var(--color-text-tertiary, #aaa)',
  textAlign: 'center',
};

const submitBtnStyle = (disabled) => ({
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  background: 'var(--color-primary)',
  color: '#fff',
  fontWeight: 700,
  fontSize: 14,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.45 : 1,
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: 'inherit',
});

function RadioOption({ label, checked, onChange }) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 4px',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: `2.5px solid ${checked ? 'var(--color-primary)' : 'var(--color-border, #ddd)'}`,
          background: checked ? 'var(--color-primary)' : 'transparent',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        aria-hidden="true"
      >
        {checked && (
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'block' }} />
        )}
      </span>
      <span style={{ fontSize: 14, color: 'var(--color-text)' }}>{label}</span>
    </label>
  );
}

export default function ReportModal({
  isOpen,
  onClose,
  contentType,
  contentId,
  contentTitle,
  character,
  worldId,
  currentPosition,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [detail, setDetail] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedIssue(null);
      setDetail('');
      setCopied(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const issueTypes = contentType === 'story' ? STORY_ISSUE_TYPES : LESSON_ISSUE_TYPES;

  const handleCopy = async () => {
    if (!selectedIssue) return;
    const issue = issueTypes.find((t) => t.key === selectedIssue);
    const text = buildReportClipboard({
      contentType,
      contentId,
      contentTitle,
      character,
      worldId,
      position: currentPosition,
      issueLabel: issue.label,
      issueDetail: detail,
      reporterEmail: user?.email || user?.displayName || 'authenticated user',
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — silent fail
    }
  };

  const handleSubmit = () => {
    if (!selectedIssue) return;
    const issue = issueTypes.find((t) => t.key === selectedIssue);
    openReportEmail({
      contentType,
      contentId,
      contentTitle,
      character,
      worldId,
      position: currentPosition,
      issueType: selectedIssue,
      issueLabel: issue.label,
      issueDetail: detail,
      reporterEmail: user?.email || user?.displayName || 'authenticated user',
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          style={overlayStyle}
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            style={cardStyle}
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {!user ? (
              /* ── STATE 1: not signed in ── */
              <>
                <div style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 4 }}>
                  <ShieldCheck size={40} style={{ color: 'var(--color-primary)' }} aria-hidden />
                </div>
                <h2 style={{ ...titleStyle, textAlign: 'center' }}>Reports are for parents</h2>
                <p style={{ ...bodyTextStyle, textAlign: 'center' }}>
                  Please sign in to submit a report. This helps us verify reports come from
                  parents, not children.
                </p>
                <AuthForm
                  compact
                  onNavigateToSignUp={() => { onClose(); navigate('/auth'); }}
                />
                <button type="button" style={cancelLinkStyle} onClick={onClose}>
                  Cancel
                </button>
              </>
            ) : (
              /* ── STATE 2: signed in, show form ── */
              <>
                <h2 style={titleStyle}>Report an issue</h2>
                <p style={subtitleStyle}>
                  {contentTitle} · {currentPosition}
                </p>
                <div role="radiogroup" aria-label="Issue type">
                  {issueTypes.map((issue) => (
                    <RadioOption
                      key={issue.key}
                      label={issue.label}
                      checked={selectedIssue === issue.key}
                      onChange={() => setSelectedIssue(issue.key)}
                    />
                  ))}
                </div>
                <div style={{ marginTop: 14 }}>
                  <label
                    htmlFor="report-detail"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--color-text-secondary, #888)',
                      marginBottom: 4,
                    }}
                  >
                    Tell us more (optional)
                  </label>
                  <textarea
                    id="report-detail"
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="Describe the issue..."
                    maxLength={500}
                    rows={3}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '8px 10px',
                      borderRadius: 10,
                      border: '1px solid var(--color-border, #ddd)',
                      background: 'transparent',
                      color: 'var(--color-text)',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={actionsRowStyle}>
                  <button type="button" style={secondaryBtnStyle} onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={submitBtnStyle(!selectedIssue)}
                    onClick={handleSubmit}
                    disabled={!selectedIssue}
                  >
                    Open Email App →
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!selectedIssue}
                  style={{
                    width: '100%',
                    marginTop: 8,
                    padding: '9px 16px',
                    borderRadius: 12,
                    border: '1px solid var(--color-border)',
                    background: copied ? 'rgba(34,197,94,0.1)' : 'transparent',
                    color: copied ? '#16a34a' : 'var(--color-text-secondary, #888)',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: !selectedIssue ? 'not-allowed' : 'pointer',
                    opacity: !selectedIssue ? 0.45 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    transition: 'background 0.15s, color 0.15s',
                    fontFamily: 'inherit',
                  }}
                >
                  {copied ? '✓ Copied!' : '📋 Copy to clipboard'}
                </button>
                <p style={noteStyle}>
                  "Open Email App" launches your mail client. No mail app? Copy and paste into Gmail or Outlook.
                </p>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
